/**
 * ReconnectingScreen — tiered recovery UI.
 *
 * Replaces the harsh "Event not found" wall the moment a guest's
 * navigation drops event context. The visual language is cinematic
 * restraint: dark glass surface, gold-tinted glow, serif headline,
 * NO bright "alert" UI that would read as "something is broken".
 *
 * Status tiers (driven by `useEventRecovery`):
 *
 *   reconnecting (t=0…1.2s):
 *      "Reconnecting to your event…"
 *      Soft pulse, no error framing.
 *
 *   restoring (t=1.2s…~4.5s):
 *      "Trying to restore your session…"
 *      Slightly louder language but still calm. We're on attempts 2-3.
 *
 *   failed (t>~4.5s):
 *      "We couldn't reconnect to this event."
 *      Three options: Try again · Return to dashboard · Rejoin event
 *
 * Props:
 *   status        — 'reconnecting' | 'restoring' | 'failed'
 *   onRetry       — () => void, retries recovery
 *   dashboardHref — string, fallback when "Return" tapped
 *   slug          — string, the slug we tried (echoed in tier-3 helper text)
 *   activeEvent   — last-known persisted event from useEventSession (optional;
 *                   when present, tier-3 shows "Rejoin {event_name}")
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

const Eyebrow = ({ children }) => (
  <p
    style={{
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: '0.32em',
      textTransform: 'uppercase',
      color: 'rgba(212,175,94,0.78)',
      marginBottom: 10,
    }}
  >
    {children}
  </p>
);

const SerifTitle = ({ children }) => (
  <h2
    style={{
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: 26,
      fontWeight: 500,
      color: '#FFFFFF',
      letterSpacing: '-0.02em',
      lineHeight: 1.15,
      marginBottom: 10,
      textShadow: '0 0 20px rgba(212,175,94,0.25)',
    }}
  >
    {children}
  </h2>
);

const Pulse = () => (
  <span
    aria-hidden
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: '#D4AF5E',
      boxShadow: '0 0 12px rgba(212,175,94,0.65)',
      animation: 'eventdropReconnectPulse 1.4s ease-in-out infinite',
      verticalAlign: 'middle',
      marginRight: 10,
    }}
  />
);

export const ReconnectingScreen = ({
  status = 'reconnecting',
  onRetry,
  dashboardHref = '/dashboard',
  slug,
  activeEvent,
}) => {
  const navigate = useNavigate();

  if (status === 'failed') {
    return (
      <div
        data-testid="event-recovery-failed"
        style={{
          minHeight: '100vh',
          background: 'radial-gradient(circle at top, #1a1a2e 0%, #0F1014 70%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: '100%',
            padding: 28,
            borderRadius: 24,
            background: 'linear-gradient(180deg, rgba(28,24,42,0.92) 0%, rgba(18,16,28,0.92) 100%)',
            border: '1px solid rgba(212,175,94,0.22)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.5), 0 0 32px rgba(212,175,94,0.12)',
            backdropFilter: 'blur(14px)',
            textAlign: 'left',
          }}
        >
          <Eyebrow>✦ Connection</Eyebrow>
          <SerifTitle>We couldn't reconnect to this event.</SerifTitle>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.62)', lineHeight: 1.5, marginBottom: 22 }}>
            The connection dropped or the link may have changed. Your moments are safe — try one of these to find your way back.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button"
              onClick={onRetry}
              data-testid="event-recovery-retry"
              style={{
                width: '100%',
                height: 48,
                borderRadius: 14,
                background: 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)',
                border: 'none',
                color: '#0F0B1A',
                fontWeight: 800,
                fontSize: 14,
                letterSpacing: '0.01em',
                cursor: 'pointer',
                boxShadow: '0 8px 26px rgba(212,175,94,0.32)',
              }}
            >
              Try again
            </button>
            {activeEvent?.dashboardHref && activeEvent.slug !== slug && (
              <button
                type="button"
                onClick={() => navigate(activeEvent.dashboardHref)}
                data-testid="event-recovery-rejoin-last"
                style={{
                  width: '100%',
                  height: 46,
                  borderRadius: 14,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: 'rgba(255,255,255,0.88)',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Rejoin {activeEvent.eventName || 'last event'}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(dashboardHref)}
              data-testid="event-recovery-dashboard"
              style={{
                width: '100%',
                height: 46,
                borderRadius: 14,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.75)',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Return to dashboard
            </button>
          </div>
          {slug && (
            <p style={{ marginTop: 18, fontSize: 11, color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>
              Reference: <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: 4 }}>{slug}</code>
            </p>
          )}
        </div>
        <style>{`
          @keyframes eventdropReconnectPulse {
            0%, 100% { opacity: 0.45; transform: scale(0.92); }
            50%      { opacity: 1;    transform: scale(1.05); }
          }
        `}</style>
      </div>
    );
  }

  // Tiers 1 & 2 — calm, glowing reassurance.
  const copy = status === 'restoring'
    ? 'Trying to restore your session…'
    : 'Reconnecting to your event…';

  return (
    <div
      data-testid={status === 'restoring' ? 'event-recovery-restoring' : 'event-recovery-reconnecting'}
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at top, #1a1a2e 0%, #0F1014 70%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 380 }}>
        <Eyebrow>✦ EventDrop</Eyebrow>
        <SerifTitle>
          <Pulse />
          {copy}
        </SerifTitle>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
          {status === 'restoring'
            ? 'Hold tight — picking up where you left off.'
            : 'Just a moment.'}
        </p>
      </div>
      <style>{`
        @keyframes eventdropReconnectPulse {
          0%, 100% { opacity: 0.45; transform: scale(0.92); }
          50%      { opacity: 1;    transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default ReconnectingScreen;
